import {
  classicBoard,
  heightAt,
  inBounds,
  SIZE,
  SUMMIT,
  HOME_SUMMITS,
  TWIN_SUMMITS,
  type SummitMode,
} from './board.js';
import { mulberry32, randomInt, type Rng } from './rng.js';

/** Version of the generator algorithm. Stored in game records; bump it on any change. */
export const GENERATOR_VERSION = 1;

export const MAX_ATTEMPTS = 400;
export const MIN_CLIFFS = 6;
export const MAX_CLIFFS = 40;

export interface GeneratedBoard {
  heights: number[][];
  seed: number;
}

/** Opposite pairs around the summit, in their order before the shuffle. */
const SUMMIT_RING: readonly (readonly [readonly [number, number], readonly [number, number]])[] = [
  [
    [3, 4],
    [5, 4],
  ],
  [
    [4, 3],
    [4, 5],
  ],
  [
    [3, 3],
    [5, 5],
  ],
  [
    [3, 5],
    [5, 3],
  ],
];

const STEPS_8: readonly (readonly [number, number])[] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/**
 * Generates a point-symmetric mountain from a seed. The same seed gives the same board
 * everywhere. Draws continue on one RNG stream across attempts; if no attempt passes the
 * checks (never seen in practice), the Classic board is returned so the result is always valid.
 */
export function generateBoard(
  seed: number,
  maxHeight: 4 | 6 = 4,
  steepSummit = false,
  summitMode: SummitMode = 'center',
): GeneratedBoard {
  if (!Number.isSafeInteger(seed)) throw new RangeError(`Seed must be an integer, got ${seed}`);
  const rng = mulberry32(seed);
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (summitMode === 'twin') {
      const heights = twinCandidate(rng, maxHeight);
      if (isAcceptable(heights, maxHeight, TWIN_SUMMITS.B)) return { heights, seed };
      continue;
    }
    if (summitMode === 'home') {
      const heights = homeCandidate(rng, maxHeight);
      if (isAcceptable(heights, maxHeight, HOME_SUMMITS.B)) return { heights, seed };
      continue;
    }
    const heights = candidate(rng, maxHeight, steepSummit);
    if (isAcceptable(heights, maxHeight)) return { heights, seed };
  }
  /* c8 ignore next */
  return { heights: classicBoard(), seed };
}

function grid(fill: number): number[][] {
  return Array.from({ length: SIZE }, () => new Array<number>(SIZE).fill(fill));
}

function at(g: number[][], r: number, c: number): number {
  return heightAt(g, r, c);
}

function set(g: number[][], r: number, c: number, value: number): void {
  const row = g[r];
  if (row === undefined || c < 0 || c >= SIZE) throw new RangeError(`(${r}, ${c}) off the board`);
  row[c] = value;
}

/**
 * One candidate board. With `maxHeight` 4 this is exactly the v0.1 algorithm. With a taller
 * summit, the terrain is stretched to 0…maxHeight-1 and the summit ring moves up with it; the
 * RNG draw order is the same either way.
 */
function candidate(rng: Rng, maxHeight: number, steepSummit: boolean): number[][] {
  const terrainTop = maxHeight - 1;
  const stretch = terrainTop / 3;
  // 1. Raw uniform values, row by row.
  const raw = grid(0);
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) set(raw, r, c, rng());

  // 2. Smoothed, centred heights 0–3.
  const h = grid(0);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      let s = 0;
      let n = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          if (!inBounds(rr, cc)) continue;
          s += at(raw, rr, cc) + at(raw, SIZE - 1 - rr, SIZE - 1 - cc);
          n += 2;
        }
      }
      const own = (at(raw, r, c) + at(raw, SIZE - 1 - r, SIZE - 1 - c)) / 2;
      const d = Math.sqrt((r - SUMMIT.r) ** 2 + (c - SUMMIT.c) ** 2);
      const v = 2.75 - 0.36 * d + (s / n - 0.5) * 6 + (own - 0.5) * 1.6;
      set(h, r, c, Math.min(terrainTop, Math.max(0, Math.round(v * stretch))));
    }
  }

  // 3. Keep the start rows low.
  for (let c = 0; c < SIZE; c++) {
    set(h, 0, c, Math.min(at(h, 0, c), 1));
    set(h, SIZE - 1, c, Math.min(at(h, SIZE - 1, c), 1));
  }

  // 4. Point symmetry and the summit.
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) set(h, SIZE - 1 - r, SIZE - 1 - c, at(h, r, c));
  }
  set(h, SUMMIT.r, SUMMIT.c, maxHeight);

  // 5. The ring around the summit: 1 or 2 ramp pairs one below the summit, the others two or
  //    three below it (heights 3 / 1–2 on a v0.1 board).
  const pairs = SUMMIT_RING.slice();
  for (let i = pairs.length - 1; i >= 1; i--) {
    const j = randomInt(rng, i + 1);
    const tmp = pairs[i];
    const other = pairs[j];
    if (tmp === undefined || other === undefined) throw new Error('unreachable');
    pairs[i] = other;
    pairs[j] = tmp;
  }
  const drawnRamps = rng() < 0.6 ? 1 : 2;
  // A steep summit has a single way up, and every other square around it is far below.
  const ramps = steepSummit ? 1 : drawnRamps;
  pairs.forEach(([a, b], index) => {
    const value =
      index < ramps
        ? maxHeight - 1
        : steepSummit
          ? maxHeight - 3
          : rng() < 0.5
            ? maxHeight - 3
            : maxHeight - 2;
    set(h, a[0], a[1], value);
    set(h, b[0], b[1], value);
  });

  return h;
}

/**
 * Two summits (experimental): Basalt's on b8 and Ivory's on h2, each reached by one or two ramp
 * squares on its inner side, with a lower ridge across the centre. Point-symmetric like every
 * Halvard mountain; the start rows stay low.
 */
function twinCandidate(rng: Rng, maxHeight: number): number[][] {
  const raw = grid(0);
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) set(raw, r, c, rng());
  const terrainTop = maxHeight - 1;
  const stretch = terrainTop / 3;
  const dist = (r: number, c: number, p: { r: number; c: number }) =>
    Math.sqrt((r - p.r) ** 2 + (c - p.c) ** 2);

  const h = grid(0);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      let s = 0;
      let n = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          if (!inBounds(rr, cc)) continue;
          s += at(raw, rr, cc) + at(raw, SIZE - 1 - rr, SIZE - 1 - cc);
          n += 2;
        }
      }
      const own = (at(raw, r, c) + at(raw, SIZE - 1 - r, SIZE - 1 - c)) / 2;
      const peak = Math.min(dist(r, c, TWIN_SUMMITS.B), dist(r, c, TWIN_SUMMITS.I));
      const ridge = dist(r, c, SUMMIT);
      const v =
        0.6 +
        2.3 * Math.max(0, 1 - peak / 4.5) +
        2.6 * Math.max(0, 1 - ridge / 3) +
        (s / n - 0.5) * 5 +
        (own - 0.5) * 1.4;
      set(h, r, c, Math.min(terrainTop, Math.max(0, Math.round(v * stretch))));
    }
  }
  for (let c = 0; c < SIZE; c++) {
    set(h, 0, c, Math.min(at(h, 0, c), 1));
    set(h, SIZE - 1, c, Math.min(at(h, SIZE - 1, c), 1));
  }
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) set(h, SIZE - 1 - r, SIZE - 1 - c, at(h, r, c));
  }

  // Around Basalt's summit (b8): the inner squares hold 1 or 2 ramps, the edge squares stay low.
  // Every change is mirrored onto Ivory's summit (h2).
  const put = (r: number, c: number, value: number) => {
    set(h, r, c, value);
    set(h, SIZE - 1 - r, SIZE - 1 - c, value);
  };
  const peak = TWIN_SUMMITS.B;
  put(peak.r, peak.c, maxHeight);
  const inner: [number, number][] = [
    [peak.r, peak.c + 1],
    [peak.r + 1, peak.c],
    [peak.r + 1, peak.c + 1],
  ];
  for (let i = inner.length - 1; i >= 1; i--) {
    const j = randomInt(rng, i + 1);
    const tmp = inner[i];
    const other = inner[j];
    if (tmp === undefined || other === undefined) throw new Error('unreachable');
    inner[i] = other;
    inner[j] = tmp;
  }
  const ramps = rng() < 0.6 ? 1 : 2;
  inner.forEach(([r, c], index) => {
    put(r, c, index < ramps ? maxHeight - 1 : rng() < 0.5 ? maxHeight - 3 : maxHeight - 2);
  });
  for (const [r, c] of [
    [peak.r - 1, peak.c - 1],
    [peak.r - 1, peak.c],
    [peak.r - 1, peak.c + 1],
    [peak.r, peak.c - 1],
    [peak.r + 1, peak.c - 1],
  ] as const) {
    put(r, c, Math.min(at(h, r, c), maxHeight - 2));
  }
  return h;
}

/**
 * Home summits (experimental): Basalt's peak on e9 and Ivory's on e1, under the Beacons' start
 * squares. Of the three squares in front of a peak, one or two are ramps one level below it and
 * the others lower; the two beside it on the back row are two or three below. Relief in the
 * centre as with two summits. Point-symmetric, and the rest of the start rows stays low.
 */
function homeCandidate(rng: Rng, maxHeight: number): number[][] {
  const h = reliefAround(rng, maxHeight, [HOME_SUMMITS.B, HOME_SUMMITS.I], 3.5);
  const put = (r: number, c: number, value: number) => {
    set(h, r, c, value);
    set(h, SIZE - 1 - r, SIZE - 1 - c, value);
  };
  const peak = HOME_SUMMITS.B;
  put(peak.r, peak.c, maxHeight);
  const front: [number, number][] = [
    [peak.r + 1, peak.c - 1],
    [peak.r + 1, peak.c],
    [peak.r + 1, peak.c + 1],
  ];
  shuffle(rng, front);
  const ramps = rng() < 0.6 ? 1 : 2;
  front.forEach(([r, c], index) => {
    put(r, c, index < ramps ? maxHeight - 1 : rng() < 0.5 ? maxHeight - 3 : maxHeight - 2);
  });
  for (const [r, c] of [
    [peak.r, peak.c - 1],
    [peak.r, peak.c + 1],
  ] as const) {
    put(r, c, rng() < 0.5 ? maxHeight - 3 : maxHeight - 2);
  }
  return h;
}

/**
 * Smoothed point-symmetric terrain that rises towards `peaks` (within `reach` squares) and to a
 * ridge across the centre, with the start rows kept low.
 */
function reliefAround(
  rng: Rng,
  maxHeight: number,
  peaks: readonly { r: number; c: number }[],
  reach: number,
): number[][] {
  const raw = grid(0);
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) set(raw, r, c, rng());
  const stretch = (maxHeight - 1) / 3;
  const dist = (r: number, c: number, p: { r: number; c: number }) =>
    Math.sqrt((r - p.r) ** 2 + (c - p.c) ** 2);
  const h = grid(0);
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      let s = 0;
      let n = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          if (!inBounds(rr, cc)) continue;
          s += at(raw, rr, cc) + at(raw, SIZE - 1 - rr, SIZE - 1 - cc);
          n += 2;
        }
      }
      const own = (at(raw, r, c) + at(raw, SIZE - 1 - r, SIZE - 1 - c)) / 2;
      const peak = Math.min(...peaks.map((p) => dist(r, c, p)));
      const v =
        0.6 +
        2.3 * Math.max(0, 1 - peak / reach) +
        2.6 * Math.max(0, 1 - dist(r, c, SUMMIT) / 3) +
        (s / n - 0.5) * 5 +
        (own - 0.5) * 1.4;
      set(h, r, c, Math.min(maxHeight - 1, Math.max(0, Math.round(v * stretch))));
    }
  }
  for (let c = 0; c < SIZE; c++) {
    set(h, 0, c, Math.min(at(h, 0, c), 1));
    set(h, SIZE - 1, c, Math.min(at(h, SIZE - 1, c), 1));
  }
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) set(h, SIZE - 1 - r, SIZE - 1 - c, at(h, r, c));
  }
  return h;
}

function shuffle<T>(rng: Rng, items: T[]): void {
  for (let i = items.length - 1; i >= 1; i--) {
    const j = randomInt(rng, i + 1);
    const tmp = items[i];
    const other = items[j];
    if (tmp === undefined || other === undefined) throw new Error('unreachable');
    items[i] = other;
    items[j] = tmp;
  }
}

function isAcceptable(
  heights: number[][],
  maxHeight: number,
  target: { r: number; c: number } = SUMMIT,
): boolean {
  const cliffs = countCliffs(heights);
  // Taller mountains have more cliffs, so the upper bound scales with the height.
  const maxCliffs = Math.round((MAX_CLIFFS * maxHeight) / 4);
  return cliffs >= MIN_CLIFFS && cliffs <= maxCliffs && summitReachable(heights, SIZE - 1, target);
}

/** Number of orthogonally adjacent pairs of squares whose heights differ by 2 or more. */
export function countCliffs(heights: number[][]): number {
  let cliffs = 0;
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const h = heightAt(heights, r, c);
      if (c + 1 < SIZE && Math.abs(h - heightAt(heights, r, c + 1)) >= 2) cliffs++;
      if (r + 1 < SIZE && Math.abs(h - heightAt(heights, r + 1, c)) >= 2) cliffs++;
    }
  }
  return cliffs;
}

/**
 * Whether the summit can be reached from Ivory's back row with 8-direction steps that climb at
 * most +1. By point symmetry the same then holds from Basalt's back row.
 */
export function summitReachable(
  heights: number[][],
  fromRow = SIZE - 1,
  target: { r: number; c: number } = SUMMIT,
): boolean {
  const seen = new Set<number>();
  const queue: [number, number][] = [];
  for (let c = 0; c < SIZE; c++) {
    seen.add(fromRow * SIZE + c);
    queue.push([fromRow, c]);
  }
  // Iterating with for-of also visits cells pushed during the loop (breadth-first order).
  for (const [r, c] of queue) {
    if (r === target.r && c === target.c) return true;
    const h = heightAt(heights, r, c);
    for (const [dr, dc] of STEPS_8) {
      const rr = r + dr;
      const cc = c + dc;
      if (!inBounds(rr, cc) || seen.has(rr * SIZE + cc)) continue;
      if (heightAt(heights, rr, cc) - h > 1) continue;
      seen.add(rr * SIZE + cc);
      queue.push([rr, cc]);
    }
  }
  return false;
}

export function isPointSymmetric(heights: number[][]): boolean {
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if (heightAt(heights, r, c) !== heightAt(heights, SIZE - 1 - r, SIZE - 1 - c)) return false;
    }
  }
  return true;
}
